import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Skeleton from 'react-loading-skeleton';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showError } from 'utils/utils';
import useModalClass from 'hooks/useModalClass';
import { getBlobUrl, getCityGalleryUrls, resolveGalleryImageUrl, resolveUploadedImageUrl } from 'utils/shared/tripBuilderImages';
import useDraggableModal from 'hooks/useDraggableModal';
import { getProposalGallery, uploadFileForProposal } from 'services/proposal';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
const SKELETON_COUNT = 9;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const MAX_IMAGE_SIZE = 1024 * 1024 * 6;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('read_failed'));
        return;
      }
      const base64 = result.split(',')[1];
      if (!base64) {
        reject(new Error('read_failed'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });

interface TripBuilderHotelImageModalProps {
  isOpen: boolean;
  blobUrl?: string;
  city?: string;
  imageUrl?: string;
  title?: string;
  onClose: () => void;
  onSave: (url: string | undefined) => void;
}

const TripBuilderHotelImageModal: FC<TripBuilderHotelImageModalProps> = ({
  isOpen,
  blobUrl: blobUrlProp,
  city,
  imageUrl,
  title = 'Add image',
  onClose,
  onSave,
}) => {
  const blobUrl = blobUrlProp || getBlobUrl();
  const classList = useModalClass(isOpen);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(isOpen);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [destinationUrls, setDestinationUrls] = useState<string[]>([]);
  const [userGalleryUrls, setUserGalleryUrls] = useState<string[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string | undefined>(imageUrl);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedUrl(imageUrl);
    setUploadedUrls([]);
    setLoading(true);

    (async () => {
      const response = await getProposalGallery();
      if (!response || response.hasErrors) {
        showError(response?.errorMessage || 'Something went wrong');
        setLoading(false);
        return;
      }

      setDestinationUrls(getCityGalleryUrls(response.companyGallery, city));

      setUserGalleryUrls(response.userGallery || []);
      setLoading(false);
    })();
  }, [city, imageUrl, isOpen]);

  const destinationPhotos = useMemo(
    () => Array.from(new Set(destinationUrls.filter(Boolean))),
    [destinationUrls],
  );

  const galleryPhotos = useMemo(() => {
    const urls = [...uploadedUrls, ...userGalleryUrls];
    if (imageUrl && !destinationPhotos.includes(imageUrl) && !urls.includes(imageUrl)) {
      urls.unshift(imageUrl);
    }
    return Array.from(new Set(urls.filter(Boolean)));
  }, [destinationPhotos, imageUrl, uploadedUrls, userGalleryUrls]);

  const renderPhotos = (urls: string[]) =>
    urls.map((url) => {
      const isSelected = selectedUrl === url;
      return (
        <div key={url} className="h-photos position-relative">
          <img
            src={resolveGalleryImageUrl(blobUrl, url)}
            alt=""
            onError={(e) => ((e.target as HTMLImageElement).parentElement!.style.display = 'none')}
            style={{ pointerEvents: 'none' }}
          />
          <div className="pr-photos-checkbox">
            <div
              onClick={() => setSelectedUrl(isSelected ? undefined : url)}
              className={'image-checkbox' + (isSelected ? ' selected' : '')}
            >
              {isSelected && <FontAwesomeIcon icon={faCheck} size="xs" />}
            </div>
          </div>
        </div>
      );
    });

  const renderSkeletons = () =>
    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
      <div key={index} className="h-photos position-relative">
        <Skeleton height="100%" />
      </div>
    ));

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || uploading) {
      return;
    }

    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      showError('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      showError('File size must be less than 6MB');
      return;
    }

    setUploading(true);
    try {
      const base64File = await readFileAsBase64(file);
      const response = await uploadFileForProposal({
        base64File,
        fileName: `trip-builder-gallery-${file.name}`,
      });
      const url = resolveUploadedImageUrl(response);
      if (!url) {
        showError('Something went wrong');
        return;
      }

      setUploadedUrls((prev) => [url, ...prev.filter((item) => item !== url)]);
      setUserGalleryUrls((prev) => [url, ...prev.filter((item) => item !== url)]);
      setSelectedUrl(url);
    } catch {
      showError('Something went wrong');
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    onSave(selectedUrl);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      ref={modalOverlayRef}
      className="modal-overlay modal-overlay-top modal-maximizable tb-gallery-modal-overlay"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div ref={modalRef} className={classList} style={{ ...modalStyle, maxWidth: '980px' }}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">{title}</h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={onClose} />
          </div>
        </div>

        <div className="modal-body modal-body-overflow">
          <input
            ref={inputRef}
            type="file"
            accept={VALID_IMAGE_TYPES.join(',')}
            className="pr-tb-image-input"
            onChange={onFileSelected}
          />
          <div
            className={'btn btn-default mb-15' + (uploading ? ' disabled' : '')}
            onClick={() => !uploading && inputRef.current?.click()}
          >
            {uploading ? 'Loading...' : 'Upload from Disk'}
          </div>

          {!!city && (loading || destinationPhotos.length > 0) && (
            <div className='mb-15'>
              <div className="title">{city}</div>
              <div className="pr-photos-wr">
                {loading ? renderSkeletons() : renderPhotos(destinationPhotos)}
              </div>
            </div>
          )}

          {(loading || galleryPhotos.length > 0) && (
            <>
              <div className="title">Gallery</div>
              <div className="pr-photos-wr">
                {loading ? renderSkeletons() : renderPhotos(galleryPhotos)}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <div className="btn btn-primary" onClick={submit}>
            Submit
          </div>
          <div className="btn btn-default" onClick={onClose}>
            Close
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TripBuilderHotelImageModal;
